import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api/client";
import {
  pullRequestApi,
  type DeployEnvironment,
  type FixJobTarget,
  type MergeInput,
  type EpicPullRequest,
  type PullRequestView,
} from "@/lib/api/pull-requests";

export const pullRequestKeys = {
  all: ["pull-requests"] as const,
  detail: (id: string) => ["pull-requests", "detail", id] as const,
  page: (id: string, page: number) => ["pull-requests", "detail", id, page] as const,
  byJob: (jobId: string) => ["pull-requests", "by-job", jobId] as const,
  byEpicRequest: (epicRequestId: string) => ["pull-requests", "by-epic-request", epicRequestId] as const,
  byEpic: (epicId: string) => ["pull-requests", "by-epic", epicId] as const,
};

/** How often the page refreshes while the pull request is still open. */
export const PULL_REQUEST_POLL_MS = 15_000;

/** A missing pull request is an answer, not a failure worth retrying. */
const retryUnlessMissing = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && (error.response.status === 404 || error.response.status === 403)) &&
  failureCount < 2;

/** The full review view. Polls only while the pull request is open. */
export function usePullRequest(id: string | undefined, page = 1) {
  return useQuery({
    queryKey: pullRequestKeys.page(id ?? "", page),
    queryFn: () => pullRequestApi.get(id ?? "", page),
    enabled: !!id,
    placeholderData: keepPreviousData,
    retry: retryUnlessMissing,
    refetchInterval: (query) => (query.state.data?.state === "open" ? PULL_REQUEST_POLL_MS : false),
  });
}

/** The pull request a job opened, if any (404 → no pull request). */
export function usePullRequestByJob(jobId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: pullRequestKeys.byJob(jobId ?? ""),
    queryFn: () => pullRequestApi.byJob(jobId ?? ""),
    enabled: !!jobId && enabled,
    retry: retryUnlessMissing,
    staleTime: 60_000,
  });
}

/** The latest pull request built from an epic request, if any (404 → none yet). */
export function usePullRequestByEpicRequest(epicRequestId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: pullRequestKeys.byEpicRequest(epicRequestId ?? ""),
    queryFn: () => pullRequestApi.byEpicRequest(epicRequestId ?? ""),
    enabled: !!epicRequestId && enabled,
    retry: retryUnlessMissing,
    staleTime: 60_000,
  });
}

/**
 * ER-130: every pull request an epic's runs opened, in merge order. Polls
 * while any is still open, so a merge on GitHub shows up without a reload.
 */
/** How often a running build's page looks for pull requests it opened. */
export const RUNNING_BUILD_PR_POLL_MS = 60_000;

/**
 * The epic's pull requests. `whileRunning` keeps looking (every minute) even
 * when none is open yet, since a running build can open one at any time.
 */
export function useEpicPullRequests(epicId: string | undefined, enabled = true, whileRunning = false) {
  return useQuery({
    queryKey: pullRequestKeys.byEpic(epicId ?? ""),
    queryFn: () => pullRequestApi.byEpic(epicId ?? ""),
    enabled: !!epicId && enabled,
    retry: retryUnlessMissing,
    refetchInterval: (query) =>
      query.state.data?.some((pr) => pr.state === "open")
        ? PULL_REQUEST_POLL_MS
        : whileRunning
          ? RUNNING_BUILD_PR_POLL_MS
          : false,
  });
}

/** ER-130: mark a draft pull request ready, so it can be merged. */
export function useMarkPullRequestReady() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pullRequestApi.markReady(id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: pullRequestKeys.all }),
  });
}

function useInvalidatePullRequest(id: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: pullRequestKeys.detail(id) });
}

export function useMergePullRequest(id: string) {
  const invalidate = useInvalidatePullRequest(id);
  return useMutation({
    mutationFn: (input: MergeInput) => pullRequestApi.merge(id, input),
    onSettled: invalidate,
  });
}

/** ENG-419: the project owner approves this head's destructive migrations; refreshes the pull request. */
export function useApproveDestructiveMigrations(id: string) {
  const invalidate = useInvalidatePullRequest(id);
  return useMutation({
    mutationFn: (input: { expectedHeadSha: string }) => pullRequestApi.approveDestructiveMigrations(id, input),
    onSettled: invalidate,
  });
}

export function useRunPullRequestReview(id: string) {
  const invalidate = useInvalidatePullRequest(id);
  return useMutation({
    mutationFn: () => pullRequestApi.review(id),
    onSettled: invalidate,
  });
}

export function useSendBackPullRequest(id: string) {
  const invalidate = useInvalidatePullRequest(id);
  return useMutation({
    mutationFn: (input: { notes: string; target: FixJobTarget }) => pullRequestApi.sendBack(id, input),
    onSuccess: invalidate,
  });
}

export function useResolvePullRequestConflicts(id: string) {
  const invalidate = useInvalidatePullRequest(id);
  return useMutation({
    mutationFn: (input: { target: FixJobTarget }) => pullRequestApi.resolveConflicts(id, input),
    onSuccess: invalidate,
  });
}

export function useDeployPullRequest(id: string) {
  const invalidate = useInvalidatePullRequest(id);
  return useMutation({
    mutationFn: (input: { environment: DeployEnvironment; confirm?: boolean }) => pullRequestApi.deploy(id, input),
    onSuccess: invalidate,
  });
}

export type { EpicPullRequest, PullRequestView };
